/**
 * Chat → Work handover.
 *
 * The intent engine can decide, while the user is still waiting on the first
 * token, that their opening message is really a job — and bind a Work run to
 * the conversation already on screen (backend/inferencing/socket_inference.js).
 * Nothing navigates, so every piece of chrome changes at once: the model
 * picker leaves, thinking power arrives, voice goes, the placeholder is
 * rewritten, and the thinking bubble becomes a live activity line. Applied in
 * a single frame that reads as a glitch rather than a decision.
 *
 * So data and presentation are split. useCoworkerChat connects to the run the
 * moment the flag flips; this hook holds the *visible* switch back until the
 * trough of a short fade, then flips everything together. The layout reflow
 * happens while the composer is invisible, so there is nothing to see popping.
 *
 * Only an upgrade mid-thread animates. A Work thread opened from history is
 * already Work on its first render and must appear instantly.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Animated, Easing } from 'react-native';

// Out fast enough to feel like a response to the send, back slowly enough to
// look intentional. The swap lands at the trough, where opacity is 0.
const FADE_OUT_MS = 130;
const FADE_IN_MS = 200;
// Matches WorkAppear's entrance so the composer and the transcript rows that
// arrive with it move as one thing.
const RISE_DISTANCE = 4;

export function useWorkModeHandover(isWorkMode, { onSwap } = {}) {
  // Work on first render is history, not a handover — start settled.
  const settledOnMount = useRef(isWorkMode).current;
  const [workMode, setWorkMode] = useState(settledOnMount);
  const [isHandingOver, setIsHandingOver] = useState(false);
  const progress = useRef(new Animated.Value(1)).current;
  const onSwapRef = useRef(onSwap);
  onSwapRef.current = onSwap;

  const commitSwap = useCallback((next) => {
    setWorkMode(next);
    try {
      onSwapRef.current?.(next);
    } catch {
      // A failed side effect must not strand the composer mid-fade.
    }
  }, []);

  // Read through a ref, and keep it out of the deps below: committing the
  // swap changes workMode, and a dep on it would tear down this very effect
  // mid-animation — cancelling the fade back in.
  const workModeRef = useRef(workMode);
  workModeRef.current = workMode;

  useEffect(() => {
    if (workModeRef.current === isWorkMode) {
      // Reached after a flag that flipped back during the fade-out: the
      // cleanup below stopped that fade part-way, so settle the composer
      // rather than leave it half-transparent with the handover flag up.
      progress.setValue(1);
      setIsHandingOver(false);
      return undefined;
    }

    // Leaving Work mode is not a flow we animate (it only happens on a store
    // correction), and dropping out of Work slowly would be worse than
    // instantly.
    if (!isWorkMode) {
      commitSwap(false);
      progress.setValue(1);
      return undefined;
    }

    let cancelled = false;
    setIsHandingOver(true);
    const fadeOut = Animated.timing(progress, {
      toValue: 0,
      duration: FADE_OUT_MS,
      easing: Easing.in(Easing.quad),
      useNativeDriver: true,
    });
    fadeOut.start(({ finished }) => {
      if (cancelled || !finished) return;
      commitSwap(true);
      Animated.timing(progress, {
        toValue: 1,
        duration: FADE_IN_MS,
        easing: Easing.out(Easing.cubic),
        useNativeDriver: true,
      }).start(({ finished: settled }) => {
        if (!cancelled && settled) setIsHandingOver(false);
      });
    });

    return () => {
      cancelled = true;
      fadeOut.stop();
    };
  }, [isWorkMode, progress, commitSwap]);

  // Unmounting mid-fade would otherwise leave the next mount at whatever
  // opacity the animation had reached.
  useEffect(() => () => progress.setValue(1), [progress]);

  const fadeStyle = useMemo(() => ({
    opacity: progress,
    transform: [{
      translateY: progress.interpolate({
        inputRange: [0, 1],
        outputRange: [RISE_DISTANCE, 0],
      }),
    }],
  }), [progress]);

  return { workMode, isHandingOver, fadeStyle };
}

export default useWorkModeHandover;
